import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { Button, Form } from "semantic-ui-react";

import { PurLookupKind, PurLookupOption } from "react-lib/apis/PUR/purLookupsApi";
import { PurClientConfig, readId } from "react-lib/apis/PUR/purRead";
import { meerpPhaseBEN } from "react-lib/localization/meerpPhaseB";

import { Event as MeERPEvent, MeERPSlice, nextMeERPRequestKey } from "./MeERPInterface";

interface PurLookupFieldProps {
  onEvent: (event: MeERPEvent) => void;
  meerp: MeERPSlice;
  // Only used to detect whether a division/token context exists; the lookup
  // itself is fetched through onEvent.
  client: PurClientConfig;
  kind: PurLookupKind; id: string; label: string;
  value: PurLookupOption | null; onChange: (value: PurLookupOption | null) => void;
  disabled?: boolean;
}
const PAGE_SIZE = 20;

const PurLookupField: React.FC<PurLookupFieldProps> = ({ onEvent, meerp, client, kind, id, label, value, onChange, disabled = false }) => {
  const intl = useIntl();
  const [search, setSearch] = useState("");
  const [request, setRequest] = useState<{ offset: number; search: string; requestKey: number } | null>(null);
  const hasContext = Boolean(client.apiToken && readId(client.divisionId) !== null);

  useEffect(() => {
    if (!request || disabled) return;
    onEvent({ message: "GetPurLookup", params: { slot: id, kind, limit: PAGE_SIZE, offset: request.offset, search: request.search, requestKey: request.requestKey } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, request, disabled]);

  const lookupState = meerp.lookups[id];
  const lookupCurrent = request !== null && lookupState?.requestKey === request.requestKey;
  const page = lookupCurrent ? lookupState.page : null;
  const loading = lookupCurrent ? lookupState.loading : Boolean(request);
  const errorId = lookupCurrent ? lookupState.errorId : null;

  const records = (page?.records || []).filter((option) => kind !== "vendors" || option.label.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase()));
  const options = value && !records.some((option) => option.id === value.id) ? [value, ...records] : records;
  const load = (offset: number) => { if (!disabled) setRequest({ offset, search, requestKey: nextMeERPRequestKey() }); };

  return <Form.Field className="card-meerp__lookup" role="group" aria-label={intl.formatMessage({ id: "MEERP_LOOKUP_GROUP" }, { name: label })}>
    <label htmlFor={id}>{label}</label>
    <div className="card-meerp__lookup-search">
      <input id={`${id}-search`} aria-label={intl.formatMessage({ id: "MEERP_LOOKUP_SEARCH" }, { name: label })} value={search} disabled={disabled} onChange={(event) => setSearch(event.target.value)} />
      <Button type="button" size="mini" disabled={disabled || !hasContext || loading} onClick={() => load(0)}>{intl.formatMessage({ id: "MEERP_LOOKUP_LOAD" })}</Button>
    </div>
    <select id={id} value={value?.id || ""} disabled={disabled} onChange={(event) => { if (!disabled) onChange(options.find((option) => String(option.id) === event.target.value) || null); }}>
      <option value="">{intl.formatMessage({ id: "MEERP_LOOKUP_CHOOSE" })}</option>
      {options.map((option) => <option key={option.id} value={option.id}>{intl.formatMessage({
        id: "MEERP_LOOKUP_OPTION_LABEL",
        // Older/still-mounted message catalogues must not hide live API labels.
        defaultMessage: meerpPhaseBEN.MEERP_LOOKUP_OPTION_LABEL,
      }, { label: option.label, id: option.id })}</option>)}
    </select>
    {!hasContext && <small>{intl.formatMessage({ id: "MEERP_LOOKUP_CONTEXT" })}</small>}
    {kind === "vendors" && <small>{intl.formatMessage({ id: "MEERP_LOOKUP_VENDOR_NOTE" })}</small>}
    {loading && <small role="status">{intl.formatMessage({ id: "MEERP_LOADING" })}</small>}
    {errorId && <small role="alert" className="card-meerp__error-text">{intl.formatMessage({ id: errorId })}</small>}
    {page && !records.length && <small>{intl.formatMessage({ id: "MEERP_LOOKUP_EMPTY" })}</small>}
    {page && request && <div className="card-meerp__lookup-pager">
      <Button type="button" size="mini" disabled={disabled || loading || request.offset === 0} onClick={() => load(Math.max(0, request.offset - PAGE_SIZE))}>{intl.formatMessage({ id: "MEERP_PREVIOUS" })}</Button>
      <small>{intl.formatMessage({ id: "MEERP_PAGE" }, { current: Math.floor(request.offset / PAGE_SIZE) + 1, total: Math.max(1, Math.ceil(page.count / PAGE_SIZE)) })}</small>
      <Button type="button" size="mini" disabled={disabled || loading || request.offset + PAGE_SIZE >= page.count} onClick={() => load(request.offset + PAGE_SIZE)}>{intl.formatMessage({ id: "MEERP_NEXT" })}</Button>
    </div>}
  </Form.Field>;
};

export default PurLookupField;
